import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Button, Chip, CircularProgress, Paper,
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { equipmentService } from '../../services/equipmentService';
import WmsScannerPanel from '../../shared/WmsScannerPanel';
import { WMS_TYPE_CHIP_SX } from '../../shared/wmsTypeChip';
import { useTranslation } from 'react-i18next';
import { PickingChannel } from './pickingChannels';

// Okutulup dogrulanmis araba. Atama (dalga olusturma) ust bilesende yapilir.
export interface ScannedCart {
  id: string;
  cartCode: string;
}

// Bu sayfada araba scanner'inin altinda listelenen siparis/urun satiri.
export interface CartScanOrderRow {
  id: string;
  primary: string;
  secondary?: string;
  done?: boolean;
  statusLabel?: string;
}

interface PickingCartScanProps {
  channel: PickingChannel;
  waveType: 'Single' | 'Multi';
  // Scanner altinda gosterilecek siparis listesi (kuyruk onizlemesi veya devam eden dalga).
  items: CartScanOrderRow[];
  // Ust bilesen kuyrugu ustlenip (claim) dalga olustururken true.
  claiming: boolean;
  // Araba okutuldu/dogrulandi (veya sepetsiz devam -> null). Ust bilesen kuyrugu bu kisiye atar.
  onClaim: (cart: ScannedCart | null) => void;
  onCancel: () => void;
  warehouseId: string;
}

// Araba okutma = atama tetikleyicisi. Toplayici arabayi okuttugunda, o an kuyrukta
// (Pending) bekleyen siparisler bu kisiye atanir ve dalga olusur. Okutmadan once
// hicbir siparis kimseye ait degildir; baska toplayicilar da ayni kuyrugu gorebilir.
const PickingCartScan: React.FC<PickingCartScanProps> = ({
  channel, waveType, items, claiming, onClaim, onCancel, warehouseId,
}) => {
  const { t } = useTranslation('warehouse');
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState('');

  const handleScan = useCallback(async (barcode: string) => {
    setValidating(true);
    setError('');
    try {
      const res = await equipmentService.getCarts(warehouseId);
      const carts: any[] = res.data?.data || res.data || [];
      const found = carts.find((c: any) => c.cartCode === barcode || c.barcodeData === barcode);
      if (!found) {
        setError(t('screens.picking.cartScan.invalidCart'));
        setValidating(false);
        return;
      }
      if (found.status !== 'Available') {
        setError(t('screens.picking.cartScan.cartInUse', { status: found.status }));
        setValidating(false);
        return;
      }
      const cartType = found.cartType ?? found.CartType;
      if (cartType && cartType !== waveType) {
        setError(t('screens.picking.cartScan.cartTypeMismatch', {
          cartType: cartType === 'Multi' ? t('screens.picking.common.multi') : t('screens.picking.common.single'),
          waveType: waveType === 'Multi' ? t('screens.picking.common.multi') : t('screens.picking.common.single'),
        }));
        setValidating(false);
        return;
      }
      onClaim({ id: found.id, cartCode: found.cartCode || barcode });
    } catch (err: any) {
      setError(err?.response?.data?.message || t('screens.picking.cartScan.assignError'));
    }
    setValidating(false);
  }, [warehouseId, waveType, onClaim, t]);

  const busy = validating || claiming;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: 1, pb: 1, maxWidth: 640, mx: 'auto', width: '100%' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Button
            size="small"
            startIcon={<ArrowBackIcon />}
            onClick={onCancel}
            disabled={busy}
            sx={{ color: 'text.secondary', flexShrink: 0 }}
          >
            {t('screens.picking.typeSelect.back')}
          </Button>
        </Box>

        {/* Kompakt baslik: kucuk araba ikonu + kanal/tip + kuyruk adedi tek satirda */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <ShoppingCartIcon sx={{ fontSize: 32, color: channel.color, flexShrink: 0 }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 800, fontSize: '1.05rem', lineHeight: 1.15 }} noWrap>
              {t('screens.picking.cartScan.title')}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.25, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">{channel.label}</Typography>
              <Chip
                size="small"
                label={waveType === 'Single' ? t('screens.picking.common.single') : t('screens.picking.common.multi')}
                color={waveType === 'Single' ? 'info' : 'secondary'}
                sx={{ ...WMS_TYPE_CHIP_SX, height: 20 }}
              />
              <Chip size="small" label={t('screens.picking.cartScan.queueCount', { count: items.length })} variant="outlined" sx={{ height: 20 }} />
            </Box>
          </Box>
        </Box>

        {busy ? (
          <Box sx={{ py: 2, textAlign: 'center' }}><CircularProgress size={28} /></Box>
        ) : (
          <WmsScannerPanel
            title={t('screens.picking.cartScan.scanTitle')}
            helperText={t('screens.picking.cartScan.claimHint')}
            onScan={handleScan}
            placeholder={t('screens.picking.cartScan.scanPlaceholder')}
            maxWidth={640}
          />
        )}

        {error && (
          <Typography color="error" sx={{ mt: 1, fontWeight: 500, textAlign: 'center' }}>{error}</Typography>
        )}

        <Box sx={{ textAlign: 'center' }}>
          <Button variant="text" color="inherit" onClick={() => onClaim(null)} disabled={busy} sx={{ mt: 0.25 }}>
            {t('screens.picking.cartScan.continueWithoutCart')}
          </Button>
        </Box>
      </Box>

      {/* Kuyruk / devam eden dalga siparis listesi */}
      <Box sx={{ flex: 1, overflow: 'auto', px: 2, pb: 2, borderTop: '1px solid', borderColor: 'divider', bgcolor: '#f5f7fb' }}>
        <Box sx={{ maxWidth: 640, mx: 'auto', pt: 1.5 }}>
          <Typography variant="overline" color="text.secondary">
            {waveType === 'Single' ? t('screens.picking.single.listTitle') : t('screens.picking.multi.listTitle')}
          </Typography>
          {items.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {t(waveType === 'Single' ? 'screens.picking.single.empty' : 'screens.picking.multi.empty')}
              </Typography>
            </Paper>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1 }}>
              {items.map((it) => (
                <Paper
                  key={it.id}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.25,
                    borderColor: it.done ? 'warning.main' : 'success.main',
                    bgcolor: it.done ? 'rgba(251,140,0,0.10)' : 'rgba(46,125,50,0.08)',
                  }}
                >
                  {it.done
                    ? <CheckCircleIcon sx={{ color: 'warning.main', flexShrink: 0 }} />
                    : <RadioButtonUncheckedIcon sx={{ color: 'success.main', flexShrink: 0 }} />}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.2 }} noWrap>
                      {it.primary}
                    </Typography>
                    {it.secondary && (
                      <Typography variant="body2" color="text.secondary" noWrap>{it.secondary}</Typography>
                    )}
                  </Box>
                  {it.statusLabel && (
                    <Chip
                      size="small"
                      label={it.statusLabel}
                      color={it.done ? 'warning' : 'success'}
                      variant={it.done ? 'filled' : 'outlined'}
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                </Paper>
              ))}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default PickingCartScan;
